import {
    ArticleIdentifier,
    QuestionnaireQuestion,
    ScipCharacteristic,
    ScipSubmissionData,
} from "../../Models/Surveys/SurveyDataModel";
import { SafeUseInstructionsScip } from "../../StateControllers/Survey/SurveyResponse/SurveyResponseModels";

export class ScipDataManager {
    public static readonly IsArticleSummaryKey = "scip_reach_is_article";

    public static readonly ScipDataKeyPrimaryArticleIdentifier: string = "primary-article-identifier";
    public static readonly ScipDataKeyArticleCategory = "article-category";
    public static readonly ScipDataKeyEuProduction = "eu-production";
    public static readonly ScipDataKeySafeUseInstructions = "safe-use-instructions";
    public static readonly ScipNumber = "scip-number";
    public static readonly HasScipNumber = "has-scip-number";
    public static readonly ScipCharacteristics = "scip-characteristics";
    public static readonly ComplexObjectOrArticleSummaryKey = "scip_complex_object_or_article";

    public static modifyScipSubmissionDataBasedOnScipDataKey(scipQuestion: QuestionnaireQuestion, scipSubmissionData: ScipSubmissionData) {
        if (scipQuestion.summaryFieldKey === ScipDataManager.IsArticleSummaryKey) {
            return;
        }

        switch (scipQuestion.scipDataKey) {
            case ScipDataManager.ScipDataKeyPrimaryArticleIdentifier:
                scipSubmissionData.primaryArticleIdentifier = this.parseArticleIdentifierValue(scipQuestion.value);
                break;
            case ScipDataManager.ScipDataKeyArticleCategory:
                scipSubmissionData.articleCategoryIds = scipQuestion.value && scipQuestion.value.length > 0 ? JSON.parse(scipQuestion.value) as string[] : [];
                break;
            case ScipDataManager.ScipDataKeyEuProduction:
                const scipProductionInEuropeanUnionAnswerToIdMap: { [index: number]: string } = {
                    [0]: "108023", // both EU produced and imported
                    [1]: "108022", // EU imported
                    [2]: "108021", // EU produced
                    [3]: "58236" // no data
                };

                const answerIndex = scipQuestion.predefinedAnswers.findIndex(t => t.nativeAnswer.toLowerCase() === scipQuestion.value.toLowerCase()) ?? 3;

                scipSubmissionData.productionInEuropeanUnionId = scipProductionInEuropeanUnionAnswerToIdMap[answerIndex];
                break;
            case ScipDataManager.ScipDataKeySafeUseInstructions:
                const safeUseInstructions = this.safeUseInstructionParse(scipQuestion.value);

                scipSubmissionData.safeUseInstructions = safeUseInstructions.safeUseInstructions;
                scipSubmissionData.noSafeUseInstructionRequired = safeUseInstructions.noSafeUseInstructionRequired;
                break;
            case ScipDataManager.ScipNumber:
                scipSubmissionData.scipNumber = scipQuestion.value;
                break;
            case ScipDataManager.HasScipNumber:
                scipSubmissionData.hasScipNumber = scipQuestion.value;
                scipSubmissionData.isScipNumberManuallyEntered = scipQuestion.value.toLowerCase() === "yes";
                break;
            case ScipDataManager.ScipCharacteristics:
                scipSubmissionData.characteristics = scipQuestion.value && scipQuestion.value.length > 0 ? JSON.parse(scipQuestion.value) as ScipCharacteristic[] : [];
                break;
            default:
                break;
        }
    }
    
    private static modifyQuestionBasedOnScipDataKey(scipQuestion: QuestionnaireQuestion, scipSubmissionData: ScipSubmissionData) {
        if (scipQuestion.summaryFieldKey === ScipDataManager.IsArticleSummaryKey) {
            return;
        }

        switch (scipQuestion.scipDataKey) {
            case ScipDataManager.ScipDataKeyPrimaryArticleIdentifier:
                scipQuestion.value = JSON.stringify(scipSubmissionData.primaryArticleIdentifier);
                break;
            case ScipDataManager.ScipDataKeyArticleCategory:
                scipQuestion.value = JSON.stringify(scipSubmissionData.articleCategoryIds);
                break;
            case ScipDataManager.ScipDataKeyEuProduction:
                const scipProductionInEuropeanUnionIdToAnswerIndexMap: { [id: string]: number } = {
                    ["108023"]: 0, // both EU produced and imported
                    ["108022"]: 1, // EU imported
                    ["108021"]: 2, // EU produced
                    [""]: 3, // no data
                    ["58236"]: 3 // no data
                };

                const answerIndex = scipProductionInEuropeanUnionIdToAnswerIndexMap[scipSubmissionData.productionInEuropeanUnionId] ?? 3;

                scipQuestion.value = scipQuestion.predefinedAnswers[answerIndex].nativeAnswer;
                break;
            case ScipDataManager.ScipDataKeySafeUseInstructions:
                const safeUseInstructions: SafeUseInstructionsScip = {
                    noSafeUseInstructionRequired: scipSubmissionData.noSafeUseInstructionRequired,
                    safeUseInstructions: scipSubmissionData.safeUseInstructions
                };

                scipQuestion.value = JSON.stringify(safeUseInstructions);
                break;
            case ScipDataManager.ScipNumber:
                scipQuestion.value = scipSubmissionData.scipNumber ?? "";
                break;
            case ScipDataManager.HasScipNumber:
                scipQuestion.value = scipSubmissionData.hasScipNumber ?? "" ;
                break;
            case ScipDataManager.ScipCharacteristics:
                scipQuestion.value = JSON.stringify(scipSubmissionData.characteristics);
                break;
            default:
                break;
        }
    }
    
    public static isScipQuestion(question: QuestionnaireQuestion){
        return (question.scipDataKey && question.scipDataKey.length > 0) || (question.summaryFieldKey === ScipDataManager.IsArticleSummaryKey);
    }
    
    public static fillScipQuestionsWithScipData(questions: QuestionnaireQuestion[], surveyScipSubmissionData: ScipSubmissionData){
        for (let question of questions) {
            if (!ScipDataManager.isScipQuestion(question)) {
                continue;
            }

            ScipDataManager.modifyQuestionBasedOnScipDataKey(question, surveyScipSubmissionData);
        }
    }

    public static safeUseInstructionParse = (value: string): SafeUseInstructionsScip => {
        try {
            return JSON.parse(value) as SafeUseInstructionsScip;
        } catch {
            return {
                noSafeUseInstructionRequired: false,
                safeUseInstructions: ""
            };
        }
    }

    public static parseArticleIdentifierValue = (value: string): ArticleIdentifier => {
        try {
            return JSON.parse(value);
        } catch {
            return {
                value: "",
                typeOtherText: "",
                identifierTypeId: ""
            };
        }
    }
}
